import * as THREE from 'three'
import { XRButton } from 'three/addons/webxr/XRButton.js';
import { OculusHandModel } from 'three/examples/jsm/webxr/OculusHandModel.js';
import gsap from 'gsap';
// Custom two-color gradient: orange → yellow
const colorA = new THREE.Color('#F99540');
const colorB = new THREE.Color('#F0F921');

let renderer;

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color( 0x505050 );

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Camera
const camera = new THREE.PerspectiveCamera( 50, window.innerWidth / window.innerHeight, 0.1, 50 );
camera.position.set( 0, 1.6, 3 );
scene.add(camera)

// Renderer
renderer = new THREE.WebGLRenderer( { antialias: true } );
renderer.setPixelRatio( window.devicePixelRatio );
renderer.setSize( window.innerWidth, window.innerHeight );
renderer.xr.enabled = true;
document.body.appendChild( renderer.domElement );

// ============================================================================
// CUSTOM WEBXR BUTTON - Create hidden XR button (we'll use the whole page instead)
// ============================================================================
const xrButton = XRButton.createButton( renderer, { 'optionalFeatures': [ 'hand-tracking' ] } );
xrButton.style.display = 'none'; // Hide the default button
document.body.appendChild( xrButton );


/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 2.4)
scene.add(ambientLight)

const light = new THREE.DirectionalLight(0xffffff, 1.0);
light.position.set(0, 10, 0);
scene.add(light);

/**
 * 3D Lissajous Curve
 */

// Toggle for additional visualization (projection spheres, connectors, axes)
const showProjectionVisualization = false;

// Lissajous parameters - classic 3D knot (3, 2, 1)
const lissajousParams = {
    freqX: 1,
    freqY: 2,
    freqZ: 3,
    phaseX: 0,
    phaseY: 0,
    phaseZ: 0,
    amplitude: 0.18
};

// Custom Lissajous curve class
class LissajousCurve extends THREE.Curve {
    constructor(params) {
        super();
        this.params = params;
    }
    
    getPoint(t, optionalTarget = new THREE.Vector3()) {
        const p = this.params;
        const angle = t * Math.PI * 2;
        
        const x = p.amplitude * Math.sin(p.freqX * angle + p.phaseX);
        const y = p.amplitude * Math.sin(p.freqY * angle + p.phaseY);
        const z = p.amplitude * Math.sin(p.freqZ * angle + p.phaseZ);
        
        return optionalTarget.set(x, y, z);
    }
}

// Create the curve and tube geometry
const lissajousCurve = new LissajousCurve(lissajousParams);
const tubularSegments = 256;
const tubeRadius = 0.006;
const radialSegments = 8;

// Blend function to smooth the seam where t wraps from 1 → 0
function wrapBlend(t, width = 0.08) {
    // width ~ fraction of tube near each end to blend
    const a = t;
    const b = t < 0.5 ? t + 1.0 : t - 1.0; // neighbor wrap
    const d = Math.min(Math.abs(t - 0.0), Math.abs(t - 1.0));

    // 0 at seam, 1 away from seam
    const w = THREE.MathUtils.smoothstep(d / width, 0, 1);

    // blend toward the "other side" near the seam
    return THREE.MathUtils.lerp(b, a, w);
}

function createLissajousTube(curve) {
    const geometry = new THREE.TubeGeometry(curve, tubularSegments, tubeRadius, radialSegments, true);

    const pos = geometry.attributes.position;
    const uv = geometry.attributes.uv;  // UV.x runs smoothly along tube length
    const colors = new Float32Array(pos.count * 3);

    const color = new THREE.Color();

    for (let i = 0; i < pos.count; i++) {
        const u = uv.getX(i); // 0..1 along tube

        // Smooth "go there and come back" so u=0 and u=1 match (no seam):
        const t = 0.5 - 0.5 * Math.cos(2 * Math.PI * u); // 0→1→0 smoothly
        
        // Lerp between orange and yellow
        color.copy(colorA).lerp(colorB, t);

        colors[i * 3 + 0] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geometry;
}

// Material with vertex colors
const lissajousMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    metalness: 0.3,
    roughness: 0.4,
    side: THREE.DoubleSide
});

const lissajousGeometry = createLissajousTube(lissajousCurve);
const lissajousMesh = new THREE.Mesh(lissajousGeometry, lissajousMaterial);

/**
 * Container Cube with Coordinate Planes
 */
const containerSize = lissajousParams.amplitude * 2.2; // Slightly larger than curve bounds
const containerGroup = new THREE.Group();

// Wireframe cube edges
const cubeGeometry = new THREE.BoxGeometry(containerSize, containerSize, containerSize);
const cubeEdges = new THREE.EdgesGeometry(cubeGeometry);
const cubeMaterial = new THREE.LineBasicMaterial({ 
    color: 0xffffff
});
const cubeWireframe = new THREE.LineSegments(cubeEdges, cubeMaterial);
containerGroup.add(cubeWireframe);

// Add Lissajous mesh inside the container
containerGroup.add(lissajousMesh);

/**
 * Follower Sphere - moves along the curve
 */
const followerGeometry = new THREE.SphereGeometry(0.012, 24, 24);
const followerMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.5,
    metalness: 0.2,
    roughness: 0.3,
    depthTest: false
});
const followerSphere = new THREE.Mesh(followerGeometry, followerMaterial);
followerSphere.renderOrder = 999; // Render last to appear on top
containerGroup.add(followerSphere);

/**
 * Projection Visualization Group - contains all additional visualization elements
 * Can be toggled with showProjectionVisualization boolean
 */
const projectionGroup = new THREE.Group();
projectionGroup.visible = showProjectionVisualization;
containerGroup.add(projectionGroup);

/**
 * Projection Spheres - move along the 3 edges (axes) of the cube
 * These show the X, Y, Z components of the main sphere's position
 */
const projectionSphereGeometry = new THREE.SphereGeometry(0.008, 16, 16);
const halfSize = containerSize / 2;

// Shared material for all projection spheres (white)
const projectionMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.3,
    metalness: 0.2,
    roughness: 0.3
});

// X-axis projection sphere (edge)
const xProjectionSphere = new THREE.Mesh(projectionSphereGeometry, projectionMaterial);
projectionGroup.add(xProjectionSphere);

// Y-axis projection sphere (edge)
const yProjectionSphere = new THREE.Mesh(projectionSphereGeometry, projectionMaterial);
projectionGroup.add(yProjectionSphere);

// Z-axis projection sphere (edge)
const zProjectionSphere = new THREE.Mesh(projectionSphereGeometry, projectionMaterial);
projectionGroup.add(zProjectionSphere);

/**
 * Plane Projection Spheres - move on the 3 faces of the cube
 * These show the 2D projections (XY, XZ, YZ) of the main sphere's position
 */

// XY plane projection sphere (on back face, Z fixed)
const xyProjectionSphere = new THREE.Mesh(projectionSphereGeometry, projectionMaterial);
projectionGroup.add(xyProjectionSphere);

// XZ plane projection sphere (on bottom face, Y fixed)
const xzProjectionSphere = new THREE.Mesh(projectionSphereGeometry, projectionMaterial);
projectionGroup.add(xzProjectionSphere);

// YZ plane projection sphere (on left face, X fixed)
const yzProjectionSphere = new THREE.Mesh(projectionSphereGeometry, projectionMaterial);
projectionGroup.add(yzProjectionSphere);

/**
 * Connector Cubes - connect 2D plane projections to 1D edge projections
 * Each 2D point connects to 2 edge points, so 6 connectors total
 */
const connectorGeometry = new THREE.BoxGeometry(1, 1, 1);
const connectorMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.2,
    metalness: 0.2,
    roughness: 0.3,
    transparent: true,
    opacity: 0.6
});
const connectorThickness = 0.003; // Thin on non-scaled axes

// XY plane to X edge (vertical connector, along Y)
const xyToXConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(xyToXConnector);

// XY plane to Y edge (horizontal connector, along X)
const xyToYConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(xyToYConnector);

// XZ plane to X edge (depth connector, along Z)
const xzToXConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(xzToXConnector);

// XZ plane to Z edge (horizontal connector, along X)
const xzToZConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(xzToZConnector);

// YZ plane to Y edge (depth connector, along Z)
const yzToYConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(yzToYConnector);

// YZ plane to Z edge (vertical connector, along Y)
const yzToZConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(yzToZConnector);

/**
 * Connectors from 2D plane projections to main sphere
 */

// XY plane to main sphere (along Z axis)
const xyToMainConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(xyToMainConnector);

// XZ plane to main sphere (along Y axis)
const xzToMainConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(xzToMainConnector);

// YZ plane to main sphere (along X axis)
const yzToMainConnector = new THREE.Mesh(connectorGeometry, connectorMaterial);
projectionGroup.add(yzToMainConnector);

/**
 * Axis Lines - thick lines along the 3 edges meeting at the cube corner
 * Using scaled cubes like the connectors
 */
const axisThickness = 0.005; // Slightly thicker than connectors

// Shared white material for all axes
const axisMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.3,
    metalness: 0.2,
    roughness: 0.3
});

// X-axis line: runs along X at Y=-halfSize, Z=-halfSize
const xAxisMesh = new THREE.Mesh(connectorGeometry, axisMaterial);
xAxisMesh.position.set(0, -halfSize, -halfSize);
xAxisMesh.scale.set(containerSize, axisThickness, axisThickness);
containerGroup.add(xAxisMesh);

// Y-axis line: runs along Y at X=-halfSize, Z=-halfSize
const yAxisMesh = new THREE.Mesh(connectorGeometry, axisMaterial);
yAxisMesh.position.set(-halfSize, 0, -halfSize);
yAxisMesh.scale.set(axisThickness, containerSize, axisThickness);
containerGroup.add(yAxisMesh);

// Z-axis line: runs along Z at X=-halfSize, Y=-halfSize
const zAxisMesh = new THREE.Mesh(connectorGeometry, axisMaterial);
zAxisMesh.position.set(-halfSize, -halfSize, 0);
zAxisMesh.scale.set(axisThickness, axisThickness, containerSize);
containerGroup.add(zAxisMesh);

/**
 * Axis Endpoint Planes - billboarded planes at the end of each axis
 * These always face the camera on 2 axes
 */
const endpointPlaneHeight = 0.045;
const endpointPlaneWidth = endpointPlaneHeight * (94 / 44); // 94:44 aspect ratio
const endpointPlaneGeometry = new THREE.PlaneGeometry(endpointPlaneWidth, endpointPlaneHeight);

// Load axis label textures
const axisTextureLoader = new THREE.TextureLoader();
const xAxisTexture = axisTextureLoader.load('/static/ui-axis-x.png');
const yAxisTexture = axisTextureLoader.load('/static/ui-axis-y.png');
const zAxisTexture = axisTextureLoader.load('/static/ui-axis-z.png');

// Create materials with transparent, unlit textures
const xEndpointMaterial = new THREE.MeshBasicMaterial({
    map: xAxisTexture,
    side: THREE.DoubleSide,
    transparent: true,
    visible: false
});

const yEndpointMaterial = new THREE.MeshBasicMaterial({
    map: yAxisTexture,
    side: THREE.DoubleSide,
    transparent: true,
    visible: false
});

const zEndpointMaterial = new THREE.MeshBasicMaterial({
    map: zAxisTexture,
    side: THREE.DoubleSide,
    transparent: true,
    visible: false
});

const axisEndpointOffset = 0.06; // Gap between axis end and plane

// X-axis endpoint plane (offset along +X from axis end)
const xEndpointPlane = new THREE.Mesh(endpointPlaneGeometry, xEndpointMaterial);
xEndpointPlane.position.set(halfSize + axisEndpointOffset, -halfSize, -halfSize);
xEndpointPlane.name = 'xEndpointPlane';
containerGroup.add(xEndpointPlane);

// Y-axis endpoint plane (offset along +Y from axis end)
const yEndpointPlane = new THREE.Mesh(endpointPlaneGeometry, yEndpointMaterial);
yEndpointPlane.position.set(-halfSize, halfSize + axisEndpointOffset, -halfSize);
yEndpointPlane.name = 'yEndpointPlane';
containerGroup.add(yEndpointPlane);

// Z-axis endpoint plane (offset along +Z from axis end)
const zEndpointPlane = new THREE.Mesh(endpointPlaneGeometry, zEndpointMaterial);
zEndpointPlane.position.set(-halfSize, -halfSize, halfSize + axisEndpointOffset);
zEndpointPlane.name = 'zEndpointPlane';
containerGroup.add(zEndpointPlane);

// Square 2D planes added to each endpoint plane
const squarePlaneSize = 0.04;
const squarePlaneGeometry = new THREE.PlaneGeometry(squarePlaneSize, squarePlaneSize);

// Load button textures
const buttonTextureLoader = new THREE.TextureLoader();
const plusTexture = buttonTextureLoader.load('/static/ui-button-plus.png');
const minusTexture = buttonTextureLoader.load('/static/ui-button-minus.png');

// Material for Up planes (plus button)
const squarePlaneUpMaterial = new THREE.MeshBasicMaterial({
    map: plusTexture,
    side: THREE.DoubleSide,
    transparent: true,
    depthTest: false
});

// Material for Down planes (minus button)
const squarePlaneDownMaterial = new THREE.MeshBasicMaterial({
    map: minusTexture,
    side: THREE.DoubleSide,
    transparent: true,
    depthTest: false
});

// Square plane for X endpoint
const xSquarePlaneUp = new THREE.Mesh(squarePlaneGeometry, squarePlaneUpMaterial);
xSquarePlaneUp.position.set(0.03, 0.044, 0);
xSquarePlaneUp.name = 'xSquarePlaneUp';
xEndpointPlane.add(xSquarePlaneUp);

// Square plane for Y endpoint
const ySquarePlaneUp = new THREE.Mesh(squarePlaneGeometry, squarePlaneUpMaterial);
ySquarePlaneUp.position.set(0.03, 0.044, 0);
ySquarePlaneUp.name = 'ySquarePlaneUp';
yEndpointPlane.add(ySquarePlaneUp);

// Square plane for Z endpoint
const zSquarePlaneUp = new THREE.Mesh(squarePlaneGeometry, squarePlaneUpMaterial);
zSquarePlaneUp.position.set(0.03, 0.044, 0);
zSquarePlaneUp.name = 'zSquarePlaneUp';
zEndpointPlane.add(zSquarePlaneUp);

// Array of square planes for easy access
const endpointSquarePlanesUp = [xSquarePlaneUp, ySquarePlaneUp, zSquarePlaneUp];

// Square planes Down - same size, positioned below
const xSquarePlaneDown = new THREE.Mesh(squarePlaneGeometry, squarePlaneDownMaterial);
xSquarePlaneDown.position.set(0.03, -0.044, 0);
xSquarePlaneDown.name = 'xSquarePlaneDown';
xEndpointPlane.add(xSquarePlaneDown);

const ySquarePlaneDown = new THREE.Mesh(squarePlaneGeometry, squarePlaneDownMaterial);
ySquarePlaneDown.position.set(0.03, -0.044, 0);
ySquarePlaneDown.name = 'ySquarePlaneDown';
yEndpointPlane.add(ySquarePlaneDown);

const zSquarePlaneDown = new THREE.Mesh(squarePlaneGeometry, squarePlaneDownMaterial);
zSquarePlaneDown.position.set(0.03, -0.044, 0);
zSquarePlaneDown.name = 'zSquarePlaneDown';
zEndpointPlane.add(zSquarePlaneDown);

// Array of square planes Down for easy access
const endpointSquarePlanesDown = [xSquarePlaneDown, ySquarePlaneDown, zSquarePlaneDown];

/**
 * Text Labels for Endpoint Planes - runtime updatable numbers
 */
function createTextCanvas(text, size = 128) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    // Transparent background
    ctx.clearRect(0, 0, size, size);
    
    // Draw text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 96px Helvetica';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, size / 2, size / 2);
    
    return canvas;
}

function createTextTexture(text) {
    const canvas = createTextCanvas(text);
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
}

// Text plane size and geometry
const textPlaneSize = 0.05;
const textPlaneGeometry = new THREE.PlaneGeometry(textPlaneSize, textPlaneSize);

// X endpoint text
const xTextTexture = createTextTexture(String(lissajousParams.freqX));
const xTextMaterial = new THREE.MeshBasicMaterial({
    map: xTextTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const freqXText = new THREE.Mesh(textPlaneGeometry, xTextMaterial);
freqXText.position.set(0.03, -0.002, -0.001);
freqXText.name = 'freqXText';
xEndpointPlane.add(freqXText);

// Y endpoint text
const yTextTexture = createTextTexture(String(lissajousParams.freqY));
const yTextMaterial = new THREE.MeshBasicMaterial({
    map: yTextTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const freqYText = new THREE.Mesh(textPlaneGeometry, yTextMaterial);
freqYText.position.set(0.03, -0.002, -0.001);
freqYText.name = 'freqYText';
yEndpointPlane.add(freqYText);

// Z endpoint text
const zTextTexture = createTextTexture(String(lissajousParams.freqZ));
const zTextMaterial = new THREE.MeshBasicMaterial({
    map: zTextTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const freqZText = new THREE.Mesh(textPlaneGeometry, zTextMaterial);
freqZText.position.set(0.03, -0.002, -0.001);
freqZText.name = 'freqZText';
zEndpointPlane.add(freqZText);

// Info label texts ("x =", "y =", "z =")
const infoXTexture = createTextTexture('x =');
const infoXMaterial = new THREE.MeshBasicMaterial({
    map: infoXTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const infoXText = new THREE.Mesh(textPlaneGeometry, infoXMaterial);
infoXText.position.set(-0.014, -0.002, 0);
infoXText.name = 'infoXText';
xEndpointPlane.add(infoXText);

const infoYTexture = createTextTexture('y =');
const infoYMaterial = new THREE.MeshBasicMaterial({
    map: infoYTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const infoYText = new THREE.Mesh(textPlaneGeometry, infoYMaterial);
infoYText.position.set(-0.014, -0.002, 0);
infoYText.name = 'infoYText';
yEndpointPlane.add(infoYText);

const infoZTexture = createTextTexture('z =');
const infoZMaterial = new THREE.MeshBasicMaterial({
    map: infoZTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const infoZText = new THREE.Mesh(textPlaneGeometry, infoZMaterial);
infoZText.position.set(-0.014, -0.002, 0);
infoZText.name = 'infoZText';
zEndpointPlane.add(infoZText);

// Store references for easy runtime updates
const textPlanes = {
    x: { plane: freqXText, material: xTextMaterial },
    y: { plane: freqYText, material: yTextMaterial },
    z: { plane: freqZText, material: zTextMaterial }
};

// Function to update text at runtime
function updateEndpointText(axis, number) {
    const textRef = textPlanes[axis];
    if (textRef) {
        const newTexture = createTextTexture(String(number));
        textRef.material.map.dispose(); // Clean up old texture
        textRef.material.map = newTexture;
        textRef.material.needsUpdate = true;
    }
}

// Array for easy iteration in animation loop
const endpointPlanes = [xEndpointPlane, yEndpointPlane, zEndpointPlane];

// Follower animation state
let followerT = 0;
const followerSpeed = 0.2; // How fast the sphere moves along the curve (cycles per second)

/**
 * Button Press State
 */
const buttonState = {
    pressThreshold: 0.025, // Distance threshold for button press
    proximityThreshold: 0.4, // Distance threshold for button visibility
    cooldown: 0.3, // Seconds between presses
    lastPressTime: {
        xUp: 0, xDown: 0,
        yUp: 0, yDown: 0,
        zUp: 0, zDown: 0
    },
    // XR startup fields for custom WebXR button
    xrReady: false,        // Prevents triggers before XR is fully initialized
    xrStartupDelay: 1.0    // Seconds to wait after XR session starts
};

// ============================================================================
// CUSTOM WEBXR BUTTON - WebXR Support Checking & Page Interaction
// ============================================================================

// Get error message element
const errorMessage = document.getElementById('webxr-error');

// Check WebXR support on page load
let webxrSupported = false;
if (navigator.xr) {
    navigator.xr.isSessionSupported('immersive-vr').then((supported) => {
        webxrSupported = supported;
        if (!supported) {
            console.log('WebXR immersive-vr not supported on this device/browser');
        }
    }).catch((error) => {
        console.error('Error checking WebXR support:', error);
        webxrSupported = false;
    });
} else {
    console.log('navigator.xr not available');
}

// Function to show WebXR error message
function showWebXRError() {
    if (errorMessage) {
        errorMessage.style.display = 'block';
        // Hide the "Start WebXR" text
        document.body.classList.add('webxr-error');
        document.body.style.cursor = 'default';
    }
}

// Make the entire page clickable for entering WebXR
document.body.style.cursor = 'pointer';
document.body.addEventListener('click', () => {
    // Don't do anything if already in XR session
    if (renderer.xr.isPresenting) {
        return;
    }
    
    // Check if WebXR is supported (synchronously, since we checked on load)
    if (!navigator.xr || !webxrSupported) {
        // WebXR not supported, show error message
        showWebXRError();
    } else {
        // WebXR is supported, proceed with session
        // Simply click the hidden XR button to trigger WebXR session
        // This uses the same logic as the original XRButton
        xrButton.click();
    }
});

// Add visual feedback for the clickable area
document.body.style.transition = 'background-color 0.3s ease';
document.body.addEventListener('mouseenter', () => {
    // Only show hover effect if not in XR
    if (!renderer.xr.isPresenting) {
        document.body.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
    }
});
document.body.addEventListener('mouseleave', () => {
    // Only clear hover effect if not in XR
    if (!renderer.xr.isPresenting) {
        document.body.style.backgroundColor = '';
    }
});

// Track XR session state to hide/show the button
renderer.xr.addEventListener('sessionstart', () => {
    document.body.classList.add('xr-presenting');
    // Wait for startup delay before enabling button interactions
    setTimeout(() => {
        buttonState.xrReady = true;
        console.log('XR ready - button interactions enabled');
    }, buttonState.xrStartupDelay * 1000);
});

renderer.xr.addEventListener('sessionend', () => {
    document.body.classList.remove('xr-presenting');
    buttonState.xrReady = false;
    // Reset body styles
    document.body.style.cursor = 'pointer';
    document.body.style.backgroundColor = '';
});

/**
 * Button Visibility State - tracks scale animation state for each endpoint
 * 'hidden' = scale 0, 'showing' = animating to scale 1, 'visible' = scale 1, 'hiding' = animating to scale 0
 */
const buttonVisibility = {
    x: { state: 'hidden', interactable: false },
    y: { state: 'hidden', interactable: false },
    z: { state: 'hidden', interactable: false }
};

// Start all buttons at scale 0
xSquarePlaneUp.scale.set(0, 0, 0);
xSquarePlaneDown.scale.set(0, 0, 0);
ySquarePlaneUp.scale.set(0, 0, 0);
ySquarePlaneDown.scale.set(0, 0, 0);
zSquarePlaneUp.scale.set(0, 0, 0);
zSquarePlaneDown.scale.set(0, 0, 0);

/**
 * Show/Hide buttons with GSAP animation
 */
function showButtons(axis) {
    const visibility = buttonVisibility[axis];
    if (visibility.state === 'visible' || visibility.state === 'showing') return;
    
    visibility.state = 'showing';
    visibility.interactable = false;
    
    let upButton, downButton;
    if (axis === 'x') { upButton = xSquarePlaneUp; downButton = xSquarePlaneDown; }
    else if (axis === 'y') { upButton = ySquarePlaneUp; downButton = ySquarePlaneDown; }
    else { upButton = zSquarePlaneUp; downButton = zSquarePlaneDown; }
    
    // Kill any existing animations
    gsap.killTweensOf(upButton.scale);
    gsap.killTweensOf(downButton.scale);
    
    // Animate to visible
    gsap.to(upButton.scale, {
        x: 1, y: 1, z: 1,
        duration: 0.2,
        ease: 'power2.out',
        onComplete: () => {
            visibility.state = 'visible';
            visibility.interactable = true;
        }
    });
    gsap.to(downButton.scale, {
        x: 1, y: 1, z: 1,
        duration: 0.2,
        ease: 'power2.out'
    });
}

function hideButtons(axis) {
    const visibility = buttonVisibility[axis];
    if (visibility.state === 'hidden' || visibility.state === 'hiding') return;
    
    visibility.state = 'hiding';
    visibility.interactable = false;
    
    let upButton, downButton;
    if (axis === 'x') { upButton = xSquarePlaneUp; downButton = xSquarePlaneDown; }
    else if (axis === 'y') { upButton = ySquarePlaneUp; downButton = ySquarePlaneDown; }
    else { upButton = zSquarePlaneUp; downButton = zSquarePlaneDown; }
    
    // Kill any existing animations
    gsap.killTweensOf(upButton.scale);
    gsap.killTweensOf(downButton.scale);
    
    // Animate to hidden
    gsap.to(upButton.scale, {
        x: 0, y: 0, z: 0,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => {
            visibility.state = 'hidden';
        }
    });
    gsap.to(downButton.scale, {
        x: 0, y: 0, z: 0,
        duration: 0.2,
        ease: 'power2.in'
    });
}

/**
 * Check finger proximity to endpoint planes and show/hide buttons accordingly
 */
function updateButtonProximity(fingerTip) {
    const fingerWorldPos = new THREE.Vector3();
    fingerTip.getWorldPosition(fingerWorldPos);
    
    const endpointWorldPos = new THREE.Vector3();
    
    // Check X endpoint proximity
    xEndpointPlane.getWorldPosition(endpointWorldPos);
    const xDist = fingerWorldPos.distanceTo(endpointWorldPos);
    if (xDist < buttonState.proximityThreshold) {
        showButtons('x');
    }
    
    // Check Y endpoint proximity
    yEndpointPlane.getWorldPosition(endpointWorldPos);
    const yDist = fingerWorldPos.distanceTo(endpointWorldPos);
    if (yDist < buttonState.proximityThreshold) {
        showButtons('y');
    }
    
    // Check Z endpoint proximity
    zEndpointPlane.getWorldPosition(endpointWorldPos);
    const zDist = fingerWorldPos.distanceTo(endpointWorldPos);
    if (zDist < buttonState.proximityThreshold) {
        showButtons('z');
    }
}

/**
 * Check if any finger is near the endpoint and hide buttons if not
 * Called once per frame with both hands checked
 */
function updateButtonHiding(leftFinger, rightFinger) {
    const leftFingerWorldPos = new THREE.Vector3();
    const rightFingerWorldPos = new THREE.Vector3();
    leftFinger.getWorldPosition(leftFingerWorldPos);
    rightFinger.getWorldPosition(rightFingerWorldPos);
    
    const endpointWorldPos = new THREE.Vector3();
    
    // Check X endpoint - hide if both hands are far
    xEndpointPlane.getWorldPosition(endpointWorldPos);
    const xDistLeft = leftFingerWorldPos.distanceTo(endpointWorldPos);
    const xDistRight = rightFingerWorldPos.distanceTo(endpointWorldPos);
    if (xDistLeft >= buttonState.proximityThreshold && xDistRight >= buttonState.proximityThreshold) {
        hideButtons('x');
    }
    
    // Check Y endpoint
    yEndpointPlane.getWorldPosition(endpointWorldPos);
    const yDistLeft = leftFingerWorldPos.distanceTo(endpointWorldPos);
    const yDistRight = rightFingerWorldPos.distanceTo(endpointWorldPos);
    if (yDistLeft >= buttonState.proximityThreshold && yDistRight >= buttonState.proximityThreshold) {
        hideButtons('y');
    }
    
    // Check Z endpoint
    zEndpointPlane.getWorldPosition(endpointWorldPos);
    const zDistLeft = leftFingerWorldPos.distanceTo(endpointWorldPos);
    const zDistRight = rightFingerWorldPos.distanceTo(endpointWorldPos);
    if (zDistLeft >= buttonState.proximityThreshold && zDistRight >= buttonState.proximityThreshold) {
        hideButtons('z');
    }
}

/**
 * Button Press Animation - visual feedback when button is pressed
 * Moves button back and scales up, then returns to original state
 */
function animateButtonPress(buttonMesh) {
    // Kill any existing animations on this button
    gsap.killTweensOf(buttonMesh.position);
    gsap.killTweensOf(buttonMesh.scale);
    
    // Animation parameters
    const pressDepth = -0.02; // How far back the button moves (local Z)
    const scaleUp = 1.3; // Scale multiplier
    const pressDuration = 0.15; // Duration of press animation
    const returnDuration = 0.15; // Duration of return animation
    const delayBeforeReturn = 0.075; // Pause at pressed state
    
    // Create timeline for sequential animations
    const tl = gsap.timeline();
    
    // Press: move back and scale up
    tl.to(buttonMesh.position, {
        z: pressDepth,
        duration: pressDuration,
        ease: 'power2.out'
    }, 0);
    
    tl.to(buttonMesh.scale, {
        x: scaleUp,
        y: scaleUp,
        duration: pressDuration,
        ease: 'power2.out'
    }, 0);
    
    // Return: move forward and scale back to original
    tl.to(buttonMesh.position, {
        z: 0,
        duration: returnDuration,
        ease: 'power2.out'
    }, pressDuration + delayBeforeReturn);
    
    tl.to(buttonMesh.scale, {
        x: 1,
        y: 1,
        duration: returnDuration,
        ease: 'power2.out'
    }, pressDuration + delayBeforeReturn);
}

// Frequency limits
const FREQ_MIN = 0;
const FREQ_MAX = 9;

/**
 * Update Lissajous Curve - regenerates geometry when frequencies change
 */
function updateLissajousCurve() {
    // Create new curve with updated params
    const newCurve = new LissajousCurve(lissajousParams);
    
    // Create new geometry
    const newGeometry = createLissajousTube(newCurve);
    
    // Dispose old geometry and assign new one
    lissajousMesh.geometry.dispose();
    lissajousMesh.geometry = newGeometry;
    
    // Update the curve reference for follower sphere
    lissajousCurve.params = lissajousParams;
}

/**
 * Composition Group - parent of all movable elements
 * This group will be moved/rotated when two hands pinch and drag
 * Positioned at the composition's center so rotation happens around this point
 */
const compositionGroup = new THREE.Group();
const compositionCenter = { x: 0, y: 1.5, z: -1.3 }; // Center of the composition
compositionGroup.position.set(compositionCenter.x, compositionCenter.y, compositionCenter.z);

// Position containerGroup relative to compositionGroup's center
containerGroup.position.set(0, 1.25 - compositionCenter.y, 0);
compositionGroup.add(containerGroup);

/**
 * Disable Frustum Culling - prevents objects from disappearing when outside camera FOV
 * Essential for WebXR where head tracking can cause unexpected culling
 */
containerGroup.traverse((child) => {
    child.frustumCulled = false;
});

/**
 * UI Plane - user-facing plane above the container
 */
const uiAspectRatio = 484 / 144;
const uiPlaneHeight = 0.17; // Adjust size as needed
const uiPlaneWidth = uiPlaneHeight * uiAspectRatio;

const uiPlaneGeometry = new THREE.PlaneGeometry(uiPlaneWidth, uiPlaneHeight);
const uiTextureLoader = new THREE.TextureLoader();
const uiTexture = uiTextureLoader.load('/static/ui-placeholder2.png');

const uiPlaneMaterial = new THREE.MeshBasicMaterial({
    map: uiTexture,
    transparent: true,
    side: THREE.DoubleSide
});

const uiPlane = new THREE.Mesh(uiPlaneGeometry, uiPlaneMaterial);
// Position relative to compositionGroup's center
uiPlane.position.set(0, 1.75 - compositionCenter.y, 0);
compositionGroup.add(uiPlane);

/**
 * Toggle UI Plane - positioned below the main UI plane
 */
const toggleAspectRatio = 220 / 60;
const togglePlaneHeight = 0.06; // 120% of original size
const togglePlaneWidth = togglePlaneHeight * toggleAspectRatio;

const togglePlaneGeometry = new THREE.PlaneGeometry(togglePlaneWidth, togglePlaneHeight);
const toggleTextureLoader = new THREE.TextureLoader();
const toggleTexture = toggleTextureLoader.load('/static/ui-toggle.png');

const togglePlaneMaterial = new THREE.MeshBasicMaterial({
    map: toggleTexture,
    transparent: true,
    side: THREE.DoubleSide,
    visible: false
});

const togglePlane = new THREE.Mesh(togglePlaneGeometry, togglePlaneMaterial);
// Position relative to compositionGroup's center
togglePlane.position.set(-0.155, 1.62 - compositionCenter.y, 0);
compositionGroup.add(togglePlane);

// Add the composition group to the scene
scene.add(compositionGroup);

// Disable frustum culling on UI planes
uiPlane.frustumCulled = false;
togglePlane.frustumCulled = false;

/**
 * Checkbox Plane on Toggle UI
 */
const toggleCheckBoxSize = 0.04;
const toggleCheckBoxGeometry = new THREE.PlaneGeometry(toggleCheckBoxSize, toggleCheckBoxSize);
const checkboxTextureLoader = new THREE.TextureLoader();
const checkboxTexture = checkboxTextureLoader.load('/static/ui-checkbox.png');
const toggleCheckBoxMaterial = new THREE.MeshBasicMaterial({
    map: checkboxTexture,
    side: THREE.DoubleSide,
    transparent: true,
    depthTest: false
});
const toggleCheckBoxPlane = new THREE.Mesh(toggleCheckBoxGeometry, toggleCheckBoxMaterial);
toggleCheckBoxPlane.position.set(-0.08, 0, 0.001);
togglePlane.add(toggleCheckBoxPlane);

// Inner checkbox plane (checkmark indicator)
const toggleCheckBoxInSize = toggleCheckBoxSize * 0.5; // 50% scale
const toggleCheckBoxInGeometry = new THREE.PlaneGeometry(toggleCheckBoxInSize, toggleCheckBoxInSize);
const checkboxInTexture = checkboxTextureLoader.load('/static/ui-checkbox-in.png');
const toggleCheckBoxInMaterial = new THREE.MeshBasicMaterial({
    map: checkboxInTexture,
    side: THREE.DoubleSide,
    transparent: true,
    depthTest: false
});
const toggleCheckBoxInPlane = new THREE.Mesh(toggleCheckBoxInGeometry, toggleCheckBoxInMaterial);
toggleCheckBoxInPlane.position.set(0, 0, 0.001); // Slightly in front
toggleCheckBoxPlane.add(toggleCheckBoxInPlane);

/**
 * Checkbox State
 */
const checkboxState = {
    isOn: false,
    lastPressTime: 0,
    cooldown: 0.3, // Same cooldown as other buttons
    pressThreshold: 0.025 // Same threshold as other buttons
};

// Start with checkbox off (inner plane hidden)
toggleCheckBoxInPlane.visible = false;

/**
 * Toggle checkbox state and update inner plane visibility
 */
function toggleCheckbox() {
    checkboxState.isOn = !checkboxState.isOn;
    toggleCheckBoxInPlane.visible = checkboxState.isOn;
    
    // Toggle projection visualization
    projectionGroup.visible = checkboxState.isOn;
}

/**
 * Projections Text on Toggle UI
 */
function createProjectionsTextCanvas() {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    
    // Transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px Helvetica';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('PROJECTIONS', canvas.width / 2, canvas.height / 2);
    
    return canvas;
}

const projectionsTextCanvas = createProjectionsTextCanvas();
const projectionsTexture = new THREE.CanvasTexture(projectionsTextCanvas);
projectionsTexture.needsUpdate = true;

const projectionsTextHeight = 0.03125;  // 125% of original size
const projectionsTextWidth = 0.25;      // 125% of original size
const projectionsTextGeometry = new THREE.PlaneGeometry(projectionsTextWidth, projectionsTextHeight);
const projectionsTextMaterial = new THREE.MeshBasicMaterial({
    map: projectionsTexture,
    side: THREE.DoubleSide,
    transparent: true
});
const projectionsTextPlane = new THREE.Mesh(projectionsTextGeometry, projectionsTextMaterial);
projectionsTextPlane.position.set(0.03, -0.001, 0.0);
togglePlane.add(projectionsTextPlane);

// Disable frustum culling on toggle plane and all its children (checkbox, inner checkbox, text)
togglePlane.traverse((child) => {
    child.frustumCulled = false;
});


/**
 * Joint References
 */
const sphere = new THREE.SphereGeometry(0.002, 32, 32)
const defaultMat = new THREE.MeshBasicMaterial({ 
    color: 0xff0000, 
    opacity: 0,
    transparent: true 
})

// Left hand joint references
const lThumbObj = new THREE.Mesh(sphere, defaultMat)
const lIndexObj = new THREE.Mesh(sphere, defaultMat)

// Right hand joint references
const rThumbObj = new THREE.Mesh(sphere, defaultMat)
const rIndexObj = new THREE.Mesh(sphere, defaultMat)

scene.add(lThumbObj)
scene.add(lIndexObj)
scene.add(rThumbObj)
scene.add(rIndexObj)

// Disable frustum culling on joint reference objects
lThumbObj.frustumCulled = false;
lIndexObj.frustumCulled = false;
rThumbObj.frustumCulled = false;
rIndexObj.frustumCulled = false;

/**
 * Pinch References
 */
const sphere2 = new THREE.SphereGeometry(0.006, 32, 32)
const defaultMat2 = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
})

// Left hand pinch sphere
const lPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
scene.add(lPinchSphere)
lPinchSphere.visible = false;

// Right hand pinch sphere
const rPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
scene.add(rPinchSphere)
rPinchSphere.visible = false;

/**
 * Pinch-and-Slide Rotation State
 * Uses camera-relative movement so rotation works correctly from any position
 */
const rotationState = {
    leftPinching: false,
    rightPinching: false,
    leftStartPos: new THREE.Vector3(),
    rightStartPos: new THREE.Vector3(),
    sensitivity: 5.0 // How fast the rotation responds to hand movement
};

/**
 * Two-Handed Movement & Rotation State
 * When both hands are pinching, move and rotate the composition around its own center
 */
const twoHandedMoveState = {
    enabled: false, // Disabled until 2 seconds after XR session starts
    active: false,
    startMidpoint: new THREE.Vector3(),
    startAngle: 0, // Angle between hands on horizontal plane
    moveSensitivity: 1.0, // Direct 1:1 movement
    rotateSensitivity: 1.0 // Direct 1:1 rotation
};

// Enable two-handed manipulation 2 seconds after XR session starts
// This prevents accidental movement when hand tracking initializes
renderer.xr.addEventListener('sessionstart', () => {
    twoHandedMoveState.enabled = false;
    setTimeout(() => {
        twoHandedMoveState.enabled = true;
    }, 2000);
});

renderer.xr.addEventListener('sessionend', () => {
    twoHandedMoveState.enabled = false;
});

/**
 * Hands
 */
var hand1, hand2;

// Hand materials remain default; no runtime material overrides

hand1 = renderer.xr.getHand( 0 );
hand1.add( new OculusHandModel( hand1 ) );
scene.add( hand1 );

hand2 = renderer.xr.getHand( 1 );
hand2.add( new OculusHandModel( hand2 ) );
scene.add( hand2 );

const handsGroup = new THREE.Group();
handsGroup.add(hand1, hand2);
scene.add(handsGroup);
handsGroup.visible = false;

// Disable frustum culling on hand tracking objects
lPinchSphere.frustumCulled = false;
rPinchSphere.frustumCulled = false;
handsGroup.traverse((child) => {
    child.frustumCulled = false;
});

// Clock for delta time
const clock = new THREE.Clock();

renderer.setAnimationLoop( function () {
    const delta = clock.getDelta();
    
    // Update GSAP ticker for WebXR compatibility
    gsap.ticker.tick();
    
    // Update follower sphere position along the curve
    followerT += followerSpeed * delta;
    if (followerT > 1) followerT -= 1; // Loop back to start
    
    const followerPosition = lissajousCurve.getPoint(followerT);
    followerSphere.position.copy(followerPosition);
    
    // Update edge projection spheres - each moves along one edge of the cube
    // They're positioned on 3 edges meeting at the corner (-halfSize, -halfSize, -halfSize)
    
    // X projection: moves along X edge, Y and Z fixed at -halfSize
    xProjectionSphere.position.set(followerPosition.x, -halfSize, -halfSize);
    
    // Y projection: moves along Y edge, X and Z fixed at -halfSize
    yProjectionSphere.position.set(-halfSize, followerPosition.y, -halfSize);
    
    // Z projection: moves along Z edge, X and Y fixed at -halfSize
    zProjectionSphere.position.set(-halfSize, -halfSize, followerPosition.z);
    
    // Update plane projection spheres - each moves on one face of the cube
    
    // XY projection: on back face (Z fixed at -halfSize)
    xyProjectionSphere.position.set(followerPosition.x, followerPosition.y, -halfSize);
    
    // XZ projection: on bottom face (Y fixed at -halfSize)
    xzProjectionSphere.position.set(followerPosition.x, -halfSize, followerPosition.z);
    
    // YZ projection: on left face (X fixed at -halfSize)
    yzProjectionSphere.position.set(-halfSize, followerPosition.y, followerPosition.z);
    
    // Update connector cubes - connect 2D projections to 1D edge projections
    
    // XY to X connector (vertical, along Y axis)
    // Connects (x, y, -halfSize) to (x, -halfSize, -halfSize)
    const xyToXLength = Math.abs(followerPosition.y - (-halfSize));
    xyToXConnector.position.set(followerPosition.x, (followerPosition.y + (-halfSize)) / 2, -halfSize);
    xyToXConnector.scale.set(connectorThickness, xyToXLength, connectorThickness);
    
    // XY to Y connector (horizontal, along X axis)
    // Connects (x, y, -halfSize) to (-halfSize, y, -halfSize)
    const xyToYLength = Math.abs(followerPosition.x - (-halfSize));
    xyToYConnector.position.set((followerPosition.x + (-halfSize)) / 2, followerPosition.y, -halfSize);
    xyToYConnector.scale.set(xyToYLength, connectorThickness, connectorThickness);
    
    // XZ to X connector (depth, along Z axis)
    // Connects (x, -halfSize, z) to (x, -halfSize, -halfSize)
    const xzToXLength = Math.abs(followerPosition.z - (-halfSize));
    xzToXConnector.position.set(followerPosition.x, -halfSize, (followerPosition.z + (-halfSize)) / 2);
    xzToXConnector.scale.set(connectorThickness, connectorThickness, xzToXLength);
    
    // XZ to Z connector (horizontal, along X axis)
    // Connects (x, -halfSize, z) to (-halfSize, -halfSize, z)
    const xzToZLength = Math.abs(followerPosition.x - (-halfSize));
    xzToZConnector.position.set((followerPosition.x + (-halfSize)) / 2, -halfSize, followerPosition.z);
    xzToZConnector.scale.set(xzToZLength, connectorThickness, connectorThickness);
    
    // YZ to Y connector (depth, along Z axis)
    // Connects (-halfSize, y, z) to (-halfSize, y, -halfSize)
    const yzToYLength = Math.abs(followerPosition.z - (-halfSize));
    yzToYConnector.position.set(-halfSize, followerPosition.y, (followerPosition.z + (-halfSize)) / 2);
    yzToYConnector.scale.set(connectorThickness, connectorThickness, yzToYLength);
    
    // YZ to Z connector (vertical, along Y axis)
    // Connects (-halfSize, y, z) to (-halfSize, -halfSize, z)
    const yzToZLength = Math.abs(followerPosition.y - (-halfSize));
    yzToZConnector.position.set(-halfSize, (followerPosition.y + (-halfSize)) / 2, followerPosition.z);
    yzToZConnector.scale.set(connectorThickness, yzToZLength, connectorThickness);
    
    // Update connectors from 2D plane projections to main sphere
    
    // XY to main connector (along Z axis)
    // Connects (x, y, -halfSize) to (x, y, z)
    const xyToMainLength = Math.abs(followerPosition.z - (-halfSize));
    xyToMainConnector.position.set(followerPosition.x, followerPosition.y, (followerPosition.z + (-halfSize)) / 2);
    xyToMainConnector.scale.set(connectorThickness, connectorThickness, xyToMainLength);
    
    // XZ to main connector (along Y axis)
    // Connects (x, -halfSize, z) to (x, y, z)
    const xzToMainLength = Math.abs(followerPosition.y - (-halfSize));
    xzToMainConnector.position.set(followerPosition.x, (followerPosition.y + (-halfSize)) / 2, followerPosition.z);
    xzToMainConnector.scale.set(connectorThickness, xzToMainLength, connectorThickness);
    
    // YZ to main connector (along X axis)
    // Connects (-halfSize, y, z) to (x, y, z)
    const yzToMainLength = Math.abs(followerPosition.x - (-halfSize));
    yzToMainConnector.position.set((followerPosition.x + (-halfSize)) / 2, followerPosition.y, followerPosition.z);
    yzToMainConnector.scale.set(yzToMainLength, connectorThickness, connectorThickness);
    
    // Billboard endpoint planes to face camera on 2 axes
    endpointPlanes.forEach(plane => {
        // Get world position of the plane
        const planeWorldPos = new THREE.Vector3();
        plane.getWorldPosition(planeWorldPos);
        
        // Make plane look at camera
        plane.lookAt(camera.position);
    });
    
    renderer.render( scene, camera );
    handTracking();
} );

function handTracking() {
    // Left Hand Joint References
    if (hand1 && hand1.joints && hand1.joints['thumb-tip']) {
        lThumbObj.position.x = hand1.joints['thumb-tip'].position.x;
        lThumbObj.position.y = hand1.joints['thumb-tip'].position.y;
        lThumbObj.position.z = hand1.joints['thumb-tip'].position.z;
        lThumbObj.rotation.x = hand1.joints['thumb-tip'].rotation.x;
        lThumbObj.rotation.y = hand1.joints['thumb-tip'].rotation.y;
        lThumbObj.rotation.z = hand1.joints['thumb-tip'].rotation.z;
    }

    if (hand1 && hand1.joints && hand1.joints['index-finger-tip']) {
        lIndexObj.position.x = hand1.joints['index-finger-tip'].position.x;
        lIndexObj.position.y = hand1.joints['index-finger-tip'].position.y;
        lIndexObj.position.z = hand1.joints['index-finger-tip'].position.z;
        lIndexObj.rotation.x = hand1.joints['index-finger-tip'].rotation.x;
        lIndexObj.rotation.y = hand1.joints['index-finger-tip'].rotation.y;
        lIndexObj.rotation.z = hand1.joints['index-finger-tip'].rotation.z;
    }

    // Right Hand Joint References
    if (hand2 && hand2.joints && hand2.joints['thumb-tip']) {
        rThumbObj.position.x = hand2.joints['thumb-tip'].position.x;
        rThumbObj.position.y = hand2.joints['thumb-tip'].position.y;
        rThumbObj.position.z = hand2.joints['thumb-tip'].position.z;
        rThumbObj.rotation.x = hand2.joints['thumb-tip'].rotation.x;
        rThumbObj.rotation.y = hand2.joints['thumb-tip'].rotation.y;
        rThumbObj.rotation.z = hand2.joints['thumb-tip'].rotation.z;
    }

    if (hand2 && hand2.joints && hand2.joints['index-finger-tip']) {
        rIndexObj.position.x = hand2.joints['index-finger-tip'].position.x;
        rIndexObj.position.y = hand2.joints['index-finger-tip'].position.y;
        rIndexObj.position.z = hand2.joints['index-finger-tip'].position.z;
        rIndexObj.rotation.x = hand2.joints['index-finger-tip'].rotation.x;
        rIndexObj.rotation.y = hand2.joints['index-finger-tip'].rotation.y;
        rIndexObj.rotation.z = hand2.joints['index-finger-tip'].rotation.z;
    }

    // Pinch Detection for Left Hand
    const lIndexThumbDist = lIndexObj.position.distanceTo(lThumbObj.position);
    const leftPinchActive = lIndexThumbDist < 0.02;

    // Pinch Detection for Right Hand
    const rIndexThumbDist = rIndexObj.position.distanceTo(rThumbObj.position);
    const rightPinchActive = rIndexThumbDist < 0.02;

    // Update pinch sphere positions
    if (leftPinchActive) {
        lPinchSphere.position.copy(lThumbObj.position);
    }
    if (rightPinchActive) {
        rPinchSphere.position.copy(rThumbObj.position);
    }

    // Validate hand tracking - check if hands are within reasonable distance from camera
    // This filters out stale/off-screen hands that keep their last position
    const maxHandDistance = 1.5; // Maximum reasonable arm reach in meters
    const leftHandDistance = lThumbObj.position.distanceTo(camera.position);
    const rightHandDistance = rThumbObj.position.distanceTo(camera.position);
    const leftHandTracked = leftHandDistance < maxHandDistance && leftHandDistance > 0.1;
    const rightHandTracked = rightHandDistance < maxHandDistance && rightHandDistance > 0.1;

    // Two-Handed Movement: When both hands are pinching, move the composition
    // Only enabled 2 seconds after XR session starts to prevent initialization jump
    // Both hands must be properly tracked (within arm's reach)
    if (twoHandedMoveState.enabled && leftPinchActive && rightPinchActive && leftHandTracked && rightHandTracked) {
        // Calculate midpoint between the two pinch points
        const currentMidpoint = new THREE.Vector3().addVectors(
            lThumbObj.position,
            rThumbObj.position
        ).multiplyScalar(0.5);

        if (!twoHandedMoveState.active) {
            // Just started two-handed pinch - record start values
            twoHandedMoveState.active = true;
            twoHandedMoveState.startMidpoint.copy(currentMidpoint);
            // Reset single-hand rotation states to prevent jump when releasing one hand
            rotationState.leftPinching = false;
            rotationState.rightPinching = false;
        } else {
            // Apply movement based on midpoint delta
            const moveDelta = new THREE.Vector3().subVectors(currentMidpoint, twoHandedMoveState.startMidpoint);
            moveDelta.multiplyScalar(twoHandedMoveState.moveSensitivity);
            compositionGroup.position.add(moveDelta);
            twoHandedMoveState.startMidpoint.copy(currentMidpoint);

            // Auto-orient composition to face the user (Y axis only)
            const directionToCamera = new THREE.Vector3();
            directionToCamera.subVectors(camera.position, compositionGroup.position);
            const targetAngle = Math.atan2(directionToCamera.x, directionToCamera.z);
            compositionGroup.rotation.y = targetAngle;
        }
    } else {
        // Not two-handed pinching - reset state
        twoHandedMoveState.active = false;

        // Single-Hand Rotation (only when NOT two-handed pinching)
        // Uses camera-relative movement so it works correctly from any user position
        
        // Get camera's right vector (local X axis in world space)
        const cameraRight = new THREE.Vector3(1, 0, 0);
        cameraRight.applyQuaternion(camera.quaternion);
        cameraRight.y = 0; // Keep it horizontal
        cameraRight.normalize();
        
        if (leftPinchActive) {
            // Handle rotation on pinch-and-slide
            if (!rotationState.leftPinching) {
                // Just started pinching - record start position
                rotationState.leftPinching = true;
                rotationState.leftStartPos.copy(lThumbObj.position);
            } else {
                // Calculate movement relative to camera's right direction
                const movement = new THREE.Vector3().subVectors(lThumbObj.position, rotationState.leftStartPos);
                const relativeX = movement.dot(cameraRight);
                containerGroup.rotation.y += relativeX * rotationState.sensitivity;
                rotationState.leftStartPos.copy(lThumbObj.position);
            }
        } else {
            rotationState.leftPinching = false;
        }

        if (rightPinchActive) {
            // Handle rotation on pinch-and-slide
            if (!rotationState.rightPinching) {
                // Just started pinching - record start position
                rotationState.rightPinching = true;
                rotationState.rightStartPos.copy(rThumbObj.position);
            } else {
                // Calculate movement relative to camera's right direction
                const movement = new THREE.Vector3().subVectors(rThumbObj.position, rotationState.rightStartPos);
                const relativeX = movement.dot(cameraRight);
                containerGroup.rotation.y += relativeX * rotationState.sensitivity;
                rotationState.rightStartPos.copy(rThumbObj.position);
            }
        } else {
            rotationState.rightPinching = false;
        }
    }
    
    // Button Proximity Detection - show/hide buttons based on finger proximity
    updateButtonProximity(lIndexObj);
    updateButtonProximity(rIndexObj);
    updateButtonHiding(lIndexObj, rIndexObj);
    
    // Button Press Detection
    checkButtonPresses(lIndexObj);
    checkButtonPresses(rIndexObj);
}

/**
 * Check if finger tip is pressing any button
 * Only allows presses when buttons are fully visible and interactable
 */
function checkButtonPresses(fingerTip) {
    const currentTime = performance.now() / 1000; // Convert to seconds
    
    // Get world positions of buttons
    const buttonWorldPos = new THREE.Vector3();
    const fingerWorldPos = new THREE.Vector3();
    fingerTip.getWorldPosition(fingerWorldPos);
    
    // X Buttons - only check if interactable
    if (buttonVisibility.x.interactable) {
        // X Up Button
        xSquarePlaneUp.getWorldPosition(buttonWorldPos);
        if (fingerWorldPos.distanceTo(buttonWorldPos) < buttonState.pressThreshold) {
            if (currentTime - buttonState.lastPressTime.xUp > buttonState.cooldown) {
                buttonState.lastPressTime.xUp = currentTime;
                animateButtonPress(xSquarePlaneUp);
                if (lissajousParams.freqX < FREQ_MAX) {
                    lissajousParams.freqX++;
                    updateEndpointText('x', lissajousParams.freqX);
                    updateLissajousCurve();
                }
            }
        }
        
        // X Down Button
        xSquarePlaneDown.getWorldPosition(buttonWorldPos);
        if (fingerWorldPos.distanceTo(buttonWorldPos) < buttonState.pressThreshold) {
            if (currentTime - buttonState.lastPressTime.xDown > buttonState.cooldown) {
                buttonState.lastPressTime.xDown = currentTime;
                animateButtonPress(xSquarePlaneDown);
                if (lissajousParams.freqX > FREQ_MIN) {
                    lissajousParams.freqX--;
                    updateEndpointText('x', lissajousParams.freqX);
                    updateLissajousCurve();
                }
            }
        }
    }
    
    // Y Buttons - only check if interactable
    if (buttonVisibility.y.interactable) {
        // Y Up Button
        ySquarePlaneUp.getWorldPosition(buttonWorldPos);
        if (fingerWorldPos.distanceTo(buttonWorldPos) < buttonState.pressThreshold) {
            if (currentTime - buttonState.lastPressTime.yUp > buttonState.cooldown) {
                buttonState.lastPressTime.yUp = currentTime;
                animateButtonPress(ySquarePlaneUp);
                if (lissajousParams.freqY < FREQ_MAX) {
                    lissajousParams.freqY++;
                    updateEndpointText('y', lissajousParams.freqY);
                    updateLissajousCurve();
                }
            }
        }
        
        // Y Down Button
        ySquarePlaneDown.getWorldPosition(buttonWorldPos);
        if (fingerWorldPos.distanceTo(buttonWorldPos) < buttonState.pressThreshold) {
            if (currentTime - buttonState.lastPressTime.yDown > buttonState.cooldown) {
                buttonState.lastPressTime.yDown = currentTime;
                animateButtonPress(ySquarePlaneDown);
                if (lissajousParams.freqY > FREQ_MIN) {
                    lissajousParams.freqY--;
                    updateEndpointText('y', lissajousParams.freqY);
                    updateLissajousCurve();
                }
            }
        }
    }
    
    // Z Buttons - only check if interactable
    if (buttonVisibility.z.interactable) {
        // Z Up Button
        zSquarePlaneUp.getWorldPosition(buttonWorldPos);
        if (fingerWorldPos.distanceTo(buttonWorldPos) < buttonState.pressThreshold) {
            if (currentTime - buttonState.lastPressTime.zUp > buttonState.cooldown) {
                buttonState.lastPressTime.zUp = currentTime;
                animateButtonPress(zSquarePlaneUp);
                if (lissajousParams.freqZ < FREQ_MAX) {
                    lissajousParams.freqZ++;
                    updateEndpointText('z', lissajousParams.freqZ);
                    updateLissajousCurve();
                }
            }
        }
        
        // Z Down Button
        zSquarePlaneDown.getWorldPosition(buttonWorldPos);
        if (fingerWorldPos.distanceTo(buttonWorldPos) < buttonState.pressThreshold) {
            if (currentTime - buttonState.lastPressTime.zDown > buttonState.cooldown) {
                buttonState.lastPressTime.zDown = currentTime;
                animateButtonPress(zSquarePlaneDown);
                if (lissajousParams.freqZ > FREQ_MIN) {
                    lissajousParams.freqZ--;
                    updateEndpointText('z', lissajousParams.freqZ);
                    updateLissajousCurve();
                }
            }
        }
    }
    
    // Checkbox Press Detection (always active, no proximity show/hide)
    toggleCheckBoxPlane.getWorldPosition(buttonWorldPos);
    if (fingerWorldPos.distanceTo(buttonWorldPos) < checkboxState.pressThreshold) {
        if (currentTime - checkboxState.lastPressTime > checkboxState.cooldown) {
            checkboxState.lastPressTime = currentTime;
            animateButtonPress(toggleCheckBoxPlane);
            toggleCheckbox();
        }
    }
}
